import { MOCK_CLUB, MOCK_PRICES, MOCK_TOURNAMENT, formatMxn } from "../mockData";
import { MockFigure, PhoneFrame } from "./frames";
import "./RegistrationPhone.css";

/**
 * M2 · Inscripción, paso 3 («Alta de jugador») del formulario público, con la
 * CURP en PDF ya leída y los datos llenados solos. También exporta el
 * encabezado del formulario (club, torneo y los 4 pasos reales), que la
 * escena del encabezado (M1) reutiliza en el paso 4.
 */

/** Pasos reales del formulario público (PublicTournamentRegistrationPage). */
const STEPS = ["Convocatoria", "Reglamento", "Alta de jugador", "Pago"] as const;

type StepNumber = 1 | 2 | 3 | 4;

function stepState(step: number, current: StepNumber): "done" | "current" | "todo" {
  if (step < current) return "done";
  return step === current ? "current" : "todo";
}

/** Palomita dibujada (sin depender de que la fuente tenga el signo ✓). */
export function MkCheck({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`mk-check ${className}`.trim()}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

/** Encabezado del formulario público: logo del club, torneo y pasos. */
export function RegistrationTop({ step }: { step: StepNumber }) {
  return (
    <div className="mk-regtop">
      <div className="mk-regtop__head">
        <span className="mk-regtop__logo">{MOCK_CLUB.initials}</span>
        <div className="mk-regtop__name">
          <strong>{MOCK_TOURNAMENT.name}</strong>
          <small>
            {MOCK_CLUB.name} · {MOCK_TOURNAMENT.shortDates}
          </small>
        </div>
      </div>
      <div className="mk-regtop__steps">
        <div className="mk-regtop__bar">
          {STEPS.map((label, index) => (
            <span key={label} className={`is-${stepState(index + 1, step)}`} />
          ))}
        </div>
        <ol>
          {STEPS.map((label, index) => {
            const state = stepState(index + 1, step);
            return (
              <li key={label} className={`is-${state}`}>
                <i>{state === "done" ? <MkCheck /> : index + 1}</i>
                <span>{label}</span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

/** Datos que la CURP llena solos (etiquetas reales del formulario). */
const AUTO_FIELDS = [
  { label: "Nombre", value: "Diego" },
  { label: "Apellido paterno", value: "Salazar" },
  { label: "Apellido materno", value: "Pérez" },
  { label: "Fecha de nacimiento", value: "14/03/1994" },
  { label: "Género", value: "Masculino" },
  { label: "Estado", value: "Jalisco" },
];

/** General $850 (elegido) y Socio Arboleda $650. */
const FEE_OPTIONS = MOCK_PRICES.slice(0, 2);

export function RegistrationPhone() {
  return (
    <MockFigure
      className="mk-regphone"
      label="Ejemplo del formulario de inscripción en el celular: el jugador subió su CURP en PDF y el formulario se llenó solo con su nombre, apellidos, fecha de nacimiento, género y estado; elige su tipo de inscripción, con el reglamento ya aceptado y la categoría validada por año de nacimiento."
    >
      <div className="mk-regphone__stage">
        <PhoneFrame className="mk-regphone__phone">
          <RegistrationTop step={3} />

          <div className="mk-regphone__title">
            <span>03</span>
            <strong>Alta de jugador</strong>
          </div>

          <div className="mk-regphone__curp">
            <small>CURP en PDF</small>
            <div className="mk-regphone__file">
              <span className="mk-regphone__pdf">PDF</span>
              <strong>CURP.pdf</strong>
              <span className="mk-regphone__read">
                <MkCheck /> Leída
              </span>
            </div>
          </div>

          <div className="mk-regphone__auto">
            <span className="mk-regphone__autotag">
              <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M12 2.5 13.9 8a2 2 0 0 0 1.2 1.2l5.4 1.8-5.4 1.9a2 2 0 0 0-1.2 1.2L12 19.5l-1.9-5.4a2 2 0 0 0-1.2-1.2L3.5 11l5.4-1.8A2 2 0 0 0 10.1 8Z"
                />
              </svg>
              Llenado automático
            </span>
            <div className="mk-regphone__fields">
              {AUTO_FIELDS.map((field) => (
                <div key={field.label} className="mk-regphone__field">
                  <small>{field.label}</small>
                  <span>{field.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mk-regphone__fee">
            <small>Tipo de inscripción</small>
            <div className="mk-regphone__options">
              {FEE_OPTIONS.map((option, index) => (
                <span key={option.label} className={index === 0 ? "mk-regphone__option is-on" : "mk-regphone__option"}>
                  <i />
                  <span>
                    <b>{option.label}</b>
                    <em>{formatMxn(option.amount)}</em>
                  </span>
                </span>
              ))}
            </div>
          </div>

          <span className="mk-regphone__save">Guardar datos</span>
        </PhoneFrame>

        <span className="mk-regphone__tag mk-regphone__tag--rules">
          <span className="mk-regphone__tagicon">
            <MkCheck />
          </span>
          <span className="mk-regphone__tagtext">Reglamento aceptado</span>
        </span>
        <span className="mk-regphone__tag mk-regphone__tag--category">
          <span className="mk-regphone__tagicon">
            <MkCheck />
          </span>
          <span className="mk-regphone__tagtext">Categoría validada por año de nacimiento</span>
        </span>
      </div>
    </MockFigure>
  );
}
